/**
 * PREDICTIA prin pereti - o ESTIMARE, nu o masuratoare. Stratul "Predicted"
 * o spune in legenda.
 *
 * Modelul multi-wall (COST-231, forma simpla):
 *
 *     PL(d) = PL0 + 10 * n * log10(d) + suma(atenuarea peretilor taiati)
 *     RSSI  = puterea AP-ului (dBm) - PL(d)
 *
 *  - PL0: pierderea la 1 m in spatiu liber, dupa banda (20·log10(f MHz) - 27.55);
 *  - n: exponentul pierderii; 3 = birou (ITU-R P.1238 da ~30 dB pe decada
 *    la 5 GHz in birouri). Cu 2 (spatiu liber) planul iesea verde peste tot:
 *    mobila, oamenii si reflexiile nu sunt pereti, dar tot mananca semnal;
 *  - fiecare perete taiat de linia dreapta AP -> punct adauga atenuarea
 *    materialului lui (WALL_DB). Un singur loc pentru constante.
 *
 * Nu stie de reflexii, de mobila, de oameni sau de etajele de deasupra. E
 * buna ca sa vezi unde lipseste un AP inainte sa-l cumperi, nu ca sa
 * inlocuiasca un survey.
 */
import { segmentsIntersect, type Pt } from './geometry'

/** Atenuarea unui perete, in dB, dupa material. */
export const WALL_DB = {
  drywall: 3,
  glass: 2,
  wood: 3,
  brick: 8,
  concrete: 12,
  metal: 20,
} as const

export type Material = keyof typeof WALL_DB

export const MATERIALS = Object.keys(WALL_DB) as Material[]

/** Pierderea la 1 m, dupa banda (2437 / 5500 / 6100 MHz). */
export const PL0_DB: Record<string, number> = { '2.4': 40.2, '5': 47.3, '6': 48.2 }

export const PATH_LOSS_EXPONENT = 3.0

/** Ce se presupune pentru un AP fara atribute. */
export const DEFAULT_TX_DBM = 20
export const DEFAULT_BAND = '5'

/** Wall in metri, cu material. */
export type WallM = { a: Pt; b: Pt; m: Material }

export type ApM = { x: number; y: number; tx_dbm?: number | null; band?: string | null }

/** Suma atenuarilor peretilor taiati de segmentul [from, to]. */
export function wallsLoss(from: Pt, to: Pt, walls: WallM[]): number {
  let loss = 0
  for (const w of walls) {
    if (segmentsIntersect(from, to, w.a, w.b)) loss += WALL_DB[w.m] ?? 0
  }
  return loss
}

/** Pierderea pe drum, in dB, la `d` metri (sub 1 m se ia 1 m). */
export function pathLoss(d: number, band: string, walls = 0): number {
  const pl0 = PL0_DB[band] ?? PL0_DB[DEFAULT_BAND]
  return pl0 + 10 * PATH_LOSS_EXPONENT * Math.log10(Math.max(1, d)) + walls
}

/** Semnalul estimat al unui AP intr-un punct (metri). */
export function predictRssi(ap: ApM, p: Pt, walls: WallM[]): number {
  const band = ap.band ?? DEFAULT_BAND
  const tx = ap.tx_dbm ?? DEFAULT_TX_DBM
  const d = Math.hypot(p.x - ap.x, p.y - ap.y)
  return tx - pathLoss(d, band, wallsLoss({ x: ap.x, y: ap.y }, p, walls))
}

/** Cel mai bun semnal estimat dintre AP-uri, sau null fara AP-uri. */
export function bestPredicted(aps: ApM[], p: Pt, walls: WallM[]): number | null {
  let best: number | null = null
  for (const ap of aps) {
    const v = predictRssi(ap, p, walls)
    if (best === null || v > best) best = v
  }
  return best
}

/**
 * Latura celulei pentru predictie: cea ceruta, marita cat trebuie ca munca
 * (celule x AP-uri x pereti) sa ramana sub `budget` - un plan de 200 m cu
 * 500 de pereti nu trebuie sa tina procesorul un minut.
 */
export function predictCell(widthM: number, heightM: number, cellM: number, aps: number, walls: number, budget = 4e7): number {
  let cell = cellM
  const work = (c: number) => Math.ceil(widthM / c) * Math.ceil(heightM / c) * Math.max(1, aps) * Math.max(1, walls)
  while (work(cell) > budget && cell < 10) cell *= 1.5
  return Math.round(cell * 100) / 100
}
